import type {
  Ancestor,
  AncestorType,
  AncestorsFilterByTypeProps,
  AncestorsFormatPathOptions,
  filterAncestorsSelfAndChildrenProps,
} from "@/entities/ancestor";

export const getOrganizationAncestor = (ancestors?: Ancestor[]) =>
  (ancestors ?? []).find((a) => a.type === "organization");

export const getLocationAncestors = (ancestors?: Ancestor[]) =>
  (ancestors ?? []).filter((a) => a.type === "location");

export const getLocationAncestor = (ancestors?: Ancestor[]) =>
  getLocationAncestors(ancestors)[0];

export const getAssetsAncestors = (ancestors?: Ancestor[]) =>
  (ancestors ?? []).filter((a) => a.type === "asset");

export const getLastAssetAncestor = (ancestors: Ancestor[]) =>
  getAssetsAncestors(ancestors).pop();

export const filterAncestorsByType = ({
  ancestors,
  includeTypes,
}: AncestorsFilterByTypeProps) => {
  return includeTypes
    ? (ancestors ?? []).filter((a) => includeTypes.includes(a.type))
    : ancestors;
};

export const formatPathFromAncestors = (
  ancestors: Ancestor[],
  { includeTypes, shouldTruncate = false }: AncestorsFormatPathOptions = {}
) => {
  const maxAncestorLength = 4;
  const filtered = filterAncestorsByType({ ancestors, includeTypes });
  if (shouldTruncate && filtered.length > maxAncestorLength) {
    const first = filtered[0].name;
    const beforeLast = filtered[filtered.length - 2].name;
    const last = filtered[filtered.length - 1].name;
    return `${first}/.../${beforeLast}/${last}`;
  }
  return filtered.map((a) => a.name).join("/");
};

export const getDirectParentByType = (
  ancestors?: Ancestor[],
  type?: AncestorType,
  excludeEntityId?: number
) => {
  if (!ancestors || !type) return undefined;

  const ancestorsOfType = ancestors.filter((a) => a.type === type);
  const filteredAncestors = ancestorsOfType.filter(
    (a) => !excludeEntityId || a.id !== excludeEntityId
  );

  // Return the last ancestor of the specified type (closest to the current entity)
  return filteredAncestors.at(-1);
};

export const filterAncestorsSelfAndChildren = ({
  ancestors,
  itemType,
  itemId,
}: filterAncestorsSelfAndChildrenProps) => {
  const filteredAncestors = filterAncestorsByType({
    ancestors,
    includeTypes: [itemType],
  });
  const isChildOrSelf = filteredAncestors.some(
    (ancestor) => ancestor.id === itemId
  );
  return !isChildOrSelf;
};
